import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useForm, useFieldArray } from 'react-hook-form';
import { useAppDispatch, useAppSelector, useIsAuthenticated } from '@/hooks/redux';
import { closeStakeModal } from '@/store/slices/modalSlice';
import useOutsideClick from '@/hooks/useOutsideClick';
import { FaPlus } from 'react-icons/fa6';
import useModalAnimation from '@/hooks/useModalAnimation';
import { useGetStakeSetQuery, useUpdateStakeSetMutation } from '@/store/api/stakesApi';
import Loader from '@/components/common/Loader';
import { toast } from 'sonner';

interface PriceRow {
  label: string;
  value: string;
}

interface FormValues {
  prices: PriceRow[];
}

type StakeTab = 'game' | 'casino';

const GAME_BUTTON_DEFAULTS: PriceRow[] = [];

const CASINO_BUTTON_DEFAULTS: PriceRow[] = [
  { label: '5', value: '5' },
  { label: '10', value: '10' },
  { label: '25', value: '25' },
  { label: '50', value: '50' },
  { label: '100', value: '100' },
  { label: '200', value: '200' },
];

const StakeModal: React.FC = () => {
  const isAuthenticated = useIsAuthenticated();
  const dispatch = useAppDispatch();
  const isStakeModalOpen = useAppSelector((state) => state.modal.isStakeModalOpen);
  const modalRef = useRef<HTMLDivElement>(null);
  const [selectedTab, setSelectedTab] = useState<StakeTab>('game');
  const [gamePrices, setGamePrices] = useState<PriceRow[]>(GAME_BUTTON_DEFAULTS);
  const [casinoPrices, setCasinoPrices] = useState<PriceRow[]>(CASINO_BUTTON_DEFAULTS);
  const { data: stakeData } = useGetStakeSetQuery(undefined, {
    skip: !isAuthenticated,
  });

  const [updateStakeSet, { isLoading: isUpdating }] = useUpdateStakeSetMutation();
  const { register, handleSubmit, control, getValues } = useForm<FormValues>({
    defaultValues: {
      prices: [],
    },
  });

  const { fields, replace } = useFieldArray({ control, name: 'prices' });

  const { animate, handleClose } = useModalAnimation({
    isOpen: isStakeModalOpen,
    onClose: () => dispatch(closeStakeModal()),
  });

  useOutsideClick(modalRef, handleClose);

  useEffect(() => {
    if (!stakeData?.stakeSet?.length) return;

    const mapped = stakeData.stakeSet.map((item) => ({
      label: String(item.label),
      value: String(item.value),
    }));

    setGamePrices(mapped);
    if (selectedTab === 'game') {
      replace(mapped);
    }
  }, [stakeData]);

  useEffect(() => {
    replace(selectedTab === 'game' ? gamePrices : casinoPrices);
  }, [selectedTab]);

  if (!isStakeModalOpen) return null;

  const handleTabSwitch = (tab: StakeTab) => {
    if (tab === selectedTab) return;

    const currentValues = getValues('prices');
    if (selectedTab === 'game') {
      setGamePrices(currentValues);
    } else {
      setCasinoPrices(currentValues);
    }

    setSelectedTab(tab);
  };

  const onSubmit = async (data: FormValues) => {
    if (selectedTab === 'game') {
      const payload = data.prices.map((item) => ({
        label: item.label,
        value: Number(item.value) || 0,
      }));

      setGamePrices(data.prices);
      await updateStakeSet({ data: payload }).unwrap();
      toast.success('Stake updated successfully');
    } else {
      setCasinoPrices(data.prices);
    }

    handleClose();
  };

  return createPortal(
    <div className="fixed font-roboto inset-0 z-[100] flex items-start justify-center bg-black/50 pt-10">
      {isUpdating && <Loader />}
      <div
        ref={modalRef}
        className={`w-full sm:max-w-[500px] bg-white shadow-xl overflow-hidden transform transition-all duration-300 ease-out ${animate ? 'translate-y-0 opacity-100' : '-translate-y-6 opacity-0'}`}
      >
        {/* Header */}
        <div className="bg-primary p-2 flex justify-between items-center border-b border-black/10">
          <h2 className="text-xl font-medium text-primary-dark-grey">Set Button Value</h2>
          <button onClick={handleClose} className="hover:opacity-70 transition-opacity">
            <FaPlus className="text-xl text-black rotate-45" />
          </button>
        </div>

        <div className="p-2">
          {/* Tab Buttons */}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => handleTabSwitch('game')}
              className={`px-4 py-2 border text-sm font-medium ${
                selectedTab === 'game' ? 'bg-[#bbb] border-gray-500' : 'bg-[#ddd] border-gray-400'
              }`}
            >
              Game Buttons
            </button>
            <button
              type="button"
              onClick={() => handleTabSwitch('casino')}
              className={`px-4 py-2 border text-sm font-medium ${
                selectedTab === 'casino' ? 'bg-[#bbb] border-gray-500' : 'bg-[#ddd] border-gray-400'
              }`}
            >
              Casino Buttons
            </button>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="pt-1">
            <div>
              {/* Table Header */}
              <div className="grid grid-cols-2 bg-primary px-3 py-1.5 text-sm font-bold border border-b-0 border-black">
                <span>Price Label</span>
                <span>Price Value</span>
              </div>

              {/* Input Rows */}
              <div className="border border-t-0 border-black">
                {fields.map((field, index) => (
                  <div
                    key={field.id}
                    className="grid grid-cols-2 gap-3 p-1.5 border-b border-black last:border-0 font-arial text-12"
                  >
                    <input
                      {...register(`prices.${index}.label` as const)}
                      type="text"
                      maxLength={15}
                      onChange={(e) => {
                        const cleaned = e.target.value.replace(/[^a-zA-Z0-9]/g, '').slice(0, 15);
                        e.target.value = cleaned;
                      }}
                      className="w-full bg-[#ddd] border border-gray-400 px-3 py-[7px] outline-none focus:border-primary"
                    />
                    <input
                      {...register(`prices.${index}.value` as const)}
                      type="number"
                      autoComplete="off"
                      autoCorrect="off"
                      onWheel={(e) => e.currentTarget.blur()}
                      onKeyDown={(e) => {
                        if (['e', 'E', '+', '-', '.'].includes(e.key)) {
                          e.preventDefault();
                        }
                      }}
                      onChange={(e) => {
                        if (Number(e.target.value) > 10000000) {
                          e.target.value = '10000000';
                        }
                      }}
                      className="w-full bg-gray-200 border border-gray-400 px-3 py-1.5 outline-none focus:border-primary"
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Submit Button */}
            <div className="mt-4">
              <button
                type="submit"
                disabled={isUpdating}
                className="w-full bg-primary py-1.5 font-arial text-base text-primary-dark-grey hover:bg-green-800 hover:text-white transition-all"
              >
                Submit
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default StakeModal;
